import { useEffect, useRef, useState } from "react";
import { X, Minus, Upload } from "lucide-react";
import { cn } from "@/lib/cn";
import { useSession } from "@/store/session-store";
import { t } from "@/lib/i18n";
import type { WebchatApi } from "@/lib/api";
import { playNotification } from "@/lib/sound";
import { Messages } from "./messages";
import { Composer } from "./composer";

interface Props {
  api: WebchatApi | null;
}

export function ChatWindow({ api }: Props) {
  const open = useSession((s) => s.open);
  const setOpen = useSession((s) => s.setOpen);
  const branding = useSession((s) => s.branding);
  const status = useSession((s) => s.status);
  const error = useSession((s) => s.error);
  const connected = useSession((s) => s.socketConnected);
  const messages = useSession((s) => s.messages);
  const updateMessageStatus = useSession((s) => s.updateMessageStatus);
  const setDroppedFile = useSession((s) => s.setDroppedFile);
  const locale = useSession((s) => s.locale);
  const position = branding?.position ?? "bottom-right";
  const playedWelcomeRef = useRef(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounter = useRef(0);

  const onDragEnter = (e: React.DragEvent) => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    dragCounter.current += 1;
    setIsDragOver(true);
  };
  const onDragOver = (e: React.DragEvent) => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
  };
  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDragOver(false);
    }
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current = 0;
    setIsDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) setDroppedFile(file);
  };

  // Mark unread inbound (from agent's perspective: outbound) messages as read
  // when the visitor opens the chat. Optimistic local update keeps the badge
  // at zero immediately; the API call propagates the receipt to the agent UI.
  useEffect(() => {
    if (!open || status !== "ready" || !api) return;
    const unreadIds = messages
      .filter((m) => m.direction === "outbound" && m.status !== "read")
      .map((m) => m.id);
    if (unreadIds.length === 0) return;
    unreadIds.forEach((id) => updateMessageStatus(id, "read"));
    void api.markRead(unreadIds).catch(() => {
      /* already optimistic-updated locally */
    });
  }, [open, status, api, messages, updateMessageStatus]);

  // Play the notification sound once when the welcome bubble first becomes
  // visible. May be silently blocked by browser autoplay rules until the
  // visitor interacts with the page.
  useEffect(() => {
    if (
      open &&
      status === "ready" &&
      messages.length === 0 &&
      branding?.greeting &&
      branding?.soundEnabled !== false &&
      !playedWelcomeRef.current
    ) {
      playedWelcomeRef.current = true;
      playNotification();
    }
  }, [open, status, messages.length, branding]);

  if (!open) return null;

  return (
    <div
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={cn(
        "fixed z-[2147483647] cz-fade-in flex flex-col",
        "w-[min(calc(100vw-48px),380px)] h-[min(calc(100vh-110px),620px)]",
        "bg-cz-surface rounded-2xl overflow-hidden",
        // Sem borda: depende só da sombra para separar do host. Reforçamos
        // com duas camadas — uma sombra longa e suave, outra mais curta
        // para dar a ambient pro card em ambos os temas.
        "shadow-[0_20px_48px_-12px_rgba(0,0,0,0.35),0_4px_12px_-2px_rgba(0,0,0,0.15)]",
        position === "bottom-right"
          ? "right-6 bottom-24"
          : "left-6 bottom-24",
      )}
    >
      {isDragOver && (
        <div className="absolute inset-0 z-50 bg-cz-primary/10 border-2 border-dashed border-cz-primary rounded-2xl flex flex-col items-center justify-center pointer-events-none">
          <Upload className="h-10 w-10 text-cz-primary mb-2" />
          <p className="text-sm font-medium text-cz-primary">
            Solte para enviar
          </p>
        </div>
      )}
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3"
        style={{ background: "var(--cz-primary)", color: "var(--cz-on-primary)" }}
      >
        <div className="flex items-center gap-3 min-w-0">
          {branding?.avatarUrl && (
            <img
              src={branding.avatarUrl}
              alt=""
              className="h-8 w-8 rounded-full object-cover flex-shrink-0"
            />
          )}
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">
              {branding?.title ?? "Chat"}
            </p>
            <p className="text-xs opacity-80 truncate flex items-center gap-1.5">
              <span
                className={cn(
                  "inline-block h-1.5 w-1.5 rounded-full",
                  connected ? "bg-green-400" : "bg-yellow-300",
                )}
              />
              {connected ? branding?.subtitle : "Reconectando…"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Minimizar"
            className="h-8 w-8 rounded-full hover:bg-black/10 flex items-center justify-center"
          >
            <Minus className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Fechar"
            className="h-8 w-8 rounded-full hover:bg-black/10 flex items-center justify-center"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {status === "loading" && (
          <div className="flex-1 flex items-center justify-center text-sm text-cz-fg-muted">
            {t("loading", locale)}
          </div>
        )}
        {status === "error" && (
          <div className="flex-1 flex items-center justify-center text-center px-6 text-sm text-red-500">
            {error ?? "Falha ao abrir chat"}
          </div>
        )}
        {status === "ready" && <Messages />}
      </div>

      {/* Composer */}
      {status === "ready" && <Composer api={api} />}

      {/* Powered by */}
      {branding?.poweredBy && (
        <div className="text-center text-[10px] py-1.5 text-cz-fg-muted bg-cz-surface-muted border-t border-cz-border">
          Powered by <span className="font-semibold">ChatZou</span>
        </div>
      )}
    </div>
  );
}
